import _conformsTo from "lodash/fp/conforms";
import _isNil from "lodash/fp/isNil";
import _negate from "lodash/fp/negate";

import {
	isKnownCodableConcept,
	isKnownCodableConceptCode,
	KnownCodableConcept,
	KnownCodableConceptCode,
} from "./concept";

/** A value that represents an actual value that is present, thus excluding `null` and `undefined`. */
export interface DataValue<T> {
	value: NonNullable<T>;
	dataAbsentReason?: undefined;
}

/** A value that represents the reason why an actual value is not present. */
export interface DataValueAbsent {
	value?: undefined;
	dataAbsentReason: KnownCodableConcept;
}

/**
 * A value that represents an actual value, or the reason it is not present.
 *
 * This is helpful for distinguishing between "data", "no data" and "data is absent for a known reason".
 */
export type DataValueOrAbsent<T> = DataValue<T> | DataValueAbsent;

/** Type guard to help in narrowing down a value to `DataValue`. */
export function isDataValue(value: unknown): value is DataValue<unknown> {
	return _conformsTo({ value: _negate(_isNil) }, value);
}

/** Type guard to help in narrowing down a value to `DataValueAbsent`. */
export function isDataValueAbsent(value: unknown): value is DataValueAbsent {
	return _conformsTo({ dataAbsentReason: isKnownCodableConcept }, value);
}

/**
 * A value that represents the reason code why an actual value is not present.
 *
 * Similar to {@link DataValueAbsent}, but with only the absent reason code.
 */
export interface DataValueAbsentCode {
	value?: undefined;
	dataAbsentReason: KnownCodableConceptCode;
}

/**
 * A value that represents an actual value, or the reason code it is not present.
 *
 * This is helpful for distinguishing between "data", "no data" and "data is absent for a known reason".
 *
 * Similar to {@link DataValueOrAbsent}, but with only the absent reason code.
 */
export type DataValueOrAbsentCode<T> = DataValue<T> | DataValueAbsentCode;

/**
 * Type guard to help in narrowing down a value to `DataValueAbsentCode`.
 *
 * Similar to {@link isDataValueAbsent}, but with only the absent reason code.
 */
export function isDataValueAbsentCode(value: unknown): value is DataValueAbsentCode {
	return _conformsTo({ dataAbsentReason: isKnownCodableConceptCode }, value);
}

/** Utility function to narrow from DataValueOrAbsent to DataValueOrAbsentCode. */
export function narrowToDataValueOrAbsentCode<T>(data: DataValueOrAbsent<T>): DataValueOrAbsentCode<T> {
	return isDataValueAbsent(data) ? { dataAbsentReason: { code: data.dataAbsentReason.coding.code } } : data;
}

/** Utility function to narrow from DataValueOrAbsent to DataValueOrAbsentCode, with narrowing the value too. */
export function narrowToDataValueOrAbsentCodeWith<T, K>(
	narrowValueTo: (value: NonNullable<T>) => NonNullable<K>,
	data: DataValueOrAbsent<T>,
): DataValueOrAbsentCode<K> {
	return isDataValueAbsent(data)
		? { dataAbsentReason: { code: data.dataAbsentReason.coding.code } }
		: { value: narrowValueTo(data.value) };
}
